import uniq from 'utils/uniq';

export const COLORED_PRODUCTS = {
  WOOD_RAILS: {
    match: id => id.match(/wooden-rail-\w+-\w+/g),
    getParams: id => {
      const [, , size, color] = id.split('-');
      return { type: 'colored-wooden-rail', color, size };
    },
  },
  CARD_ENVELOPES: {
    match: id => id.match(/envelope-\w+-\w+-\w+/g),
    getParams: id => {
      const [, size, color, content] = id.split('-');
      return { type: 'colored-card-envelope', size, color, content };
    },
  },
};

export function isListOfColoredDisplayProducts(productIds = []) {
  let value = false;
  Object.values(COLORED_PRODUCTS).forEach(type => {
    if (productIds.every(id => type.match(id))) {
      value = true;
    }
  });
  return value;
}

export function getParamsOfColoredDisplayProduct(productId) {
  return Object.values(COLORED_PRODUCTS).reduce((acc, item) => {
    if (item.match(productId)) {
      acc = item.getParams(productId);
    }
    return acc;
  }, null);
}

export function getColoredDisplayProductIds(productIds, options = {}) {
  return productIds.filter(productId => {
    // TODO is there any better way how to filter suitable products?
    return productId.indexOf(options.color) >= 0;
  });
}

const prepareProducts = productOrProducts => {
  if (Array.isArray(productOrProducts)) {
    return productOrProducts;
  }
  return [productOrProducts];
};

export const getProduct = (productOrProducts, productId) => {
  return prepareProducts(productOrProducts).find(p => p.id === productId);
};

export const getProductName = (productOrProducts, productId) => {
  const product = getProduct(productOrProducts, productId);

  if (product) {
    return product.name;
  }

  return productId;
};

export function getDefaultSetCount(product) {
  if (product.pricingSettings) {
    return product.pricingSettings.default_set_count || 0;
  }
  return 0;
}

export function getPagePerSet(product) {
  if (product.pricingSettings) {
    return product.pricingSettings.page_per_set || 1;
  }
  return 1;
}

export function getPricePerSet(product) {
  if (product.pricePerSet) {
    return product.pricePerSet || 0;
  }
  return 0;
}

export function getExtraSetCount(product, pagesCount) {
  if (product) {
    const defaultSetCount = getDefaultSetCount(product);
    const pagePerSet = getPagePerSet(product);

    if (defaultSetCount > 0) {
      return pagesCount / pagePerSet - defaultSetCount;
    }
  }
  return 0;
}

export function getPriceData(item, index, priceData = null) {
  let currency;
  let unitPrice;
  let totalPrice;
  let otherPrice;
  let perSetPrice;
  let extraSetCount;
  let delayedDelivery = false;
  const quantity = item.quantity;
  const isEditable = item.editable && item.product.editable;

  if (item.data && item.data.pages) {
    extraSetCount = getExtraSetCount(item.product, item.data.pages.length);
  }

  if (priceData && priceData.items[index]) {
    currency = priceData.currency;
    unitPrice = priceData.items[index].single;
    totalPrice = priceData.items[index].all;
    otherPrice = priceData.items[index].other || null;
    perSetPrice = priceData.items[index].per_set || null;
    delayedDelivery = priceData.items[index].lab_issue || false;

    if (priceData.items[index].tiers) {
      const currentTier = Object.keys(priceData.items[index].tiers)
        .map(val => parseInt(val, 10))
        .sort((a, b) => b - a)
        .find(tier => quantity >= tier);
      unitPrice = priceData.items[index].tiers[`${currentTier}`] ?? unitPrice;
    }
  }

  return {
    currency,
    unitPrice,
    totalPrice,
    otherPrice,
    perSetPrice,
    extraSetCount,
    delayedDelivery,
    quantity,
    isEditable,
  };
}

export function getTotalPrice(product, numberOfPages) {
  if (product) {
    const extraSetCount = getExtraSetCount(product, numberOfPages);
    const pricePerSet = getPricePerSet(product);

    // when numberOfPages is less than minimumNuberOfPages, use product.price
    return Math.max(product.price, product.price + extraSetCount * pricePerSet);
  }
  return 0;
}

/**
 * Build a user-facing price breakdown for a product + current page count.
 * Returns the base price and — for additional-page products (books,
 * calendars configured with per-set pricing) — the number of extra pages
 * and the additional cost they contribute.
 *
 * `basePagesCount` is the number of pages included in `product.price`
 * (`default_set_count * page_per_set`). `additionalPages` and
 * `additionalPrice` are `0` when the draft is at or under the base count —
 * callers can branch on `additionalPages > 0` to decide whether to render
 * the extras line.
 */
export function getPriceBreakdown(product, numberOfPages) {
  if (!product) {
    return {
      basePrice: 0,
      basePagesCount: 0,
      additionalPages: 0,
      additionalPrice: 0,
      totalPrice: 0,
    };
  }

  const defaultSetCount = getDefaultSetCount(product);
  const pagePerSet = getPagePerSet(product);
  const pricePerSet = getPricePerSet(product);
  const extraSetCount = Math.max(0, getExtraSetCount(product, numberOfPages));

  return {
    basePrice: product.price,
    basePagesCount: defaultSetCount * pagePerSet,
    additionalPages: extraSetCount * pagePerSet,
    additionalPrice: extraSetCount * pricePerSet,
    totalPrice: getTotalPrice(product, numberOfPages),
  };
}

export function getQuantityUnit(product) {
  let quantityUnit = 'single';
  if (product) {
    quantityUnit = product.quantity_unit;
  }
  return quantityUnit;
}

export function hasProductOptions(product) {
  // used in relatedProducts to group similar products
  if (product.skuProductOptions) {
    if (Object.keys(product.skuProductOptions || {}).length > 0) {
      return true;
    }
  }
  // used in old approach of wooden-rails
  if (product.templateMetaData) {
    if (Object.keys(product.templateMetaData.options || {}).length > 0) {
      return true;
    }
  }
  return false;
}

export function haveProductsOptions(products = []) {
  let value = false;

  if (isListOfColoredDisplayProducts(products.map(product => product.id))) {
    return true;
  }

  products.forEach(product => {
    if (!value && product.templateMetaData) {
      value = Object.keys(product.templateMetaData.options || {}).length > 0;
    }
  });
  return value;
}

export function getProductOptions(product) {
  // used in relatedProducts to group similar products
  if (product.skuProductOptions) {
    const options = Object.entries(product.skuProductOptions || {});
    if (options.length > 0) {
      return options.map(([key, values]) => {
        return [key, Object.keys(values)];
      });
    }
  }
  // used in old approach of wooden-rails
  if (product.templateMetaData) {
    const options = Object.entries(product.templateMetaData.options || {});
    if (options.length > 0) {
      return options;
    }
  }
  return [];
}

export function getProductsOptions(products = []) {
  const options = {};

  if (isListOfColoredDisplayProducts(products.map(product => product.id))) {
    products.forEach(product => {
      const { color } = getParamsOfColoredDisplayProduct(product.id);
      if (!options.color) {
        options.color = [];
      }
      options.color = uniq([...options.color, color]);
    });
  } else {
    products.forEach(product => {
      if (product.templateMetaData) {
        Object.entries(product.templateMetaData.options || {}).forEach(([key, values]) => {
          if (options[key]) {
            options[key] = uniq([...options[key], ...values]);
          } else {
            options[key] = values;
          }
        });
      }
    });
  }
  return Object.entries(options);
}

export function hasValidProductOptions(product) {
  if (!product) {
    return false;
  }

  let isValid = true;
  getProductOptions(product).forEach(([, values]) => {
    if (!values || values.length === 0) {
      isValid = false;
    }
  });
  return isValid;
}

export function getProductDescription(product, full = false) {
  const { description } = product;

  if (description) {
    if (full) {
      return description;
    }

    const separatorIndex = description.indexOf('\n\n');
    if (separatorIndex < 0) {
      return description;
    }

    return description.substring(0, separatorIndex);
  }

  return '';
}

export function getProductPrice(product, data = {}) {
  let price = 0;

  price += product.price;

  if (data.pages && product.pricing_settings) {
    const additionalPages = data.pages.length - product.pricing_settings.default_set_count;

    if (additionalPages > 0) {
      const pagePerSet = product.pricing_settings.page_per_set;
      const pricePerSet = product.price_per_set;
      const additionalPrice = (additionalPages / pagePerSet) * pricePerSet;

      price += additionalPrice;
    }
  }

  return price;
}

export function getOptionImageSrcSet(templateRootPath, option, value) {
  const url = `${templateRootPath}/assets_client/options/${String(option).toLowerCase()}-${String(
    value,
  ).toLowerCase()}`;

  return `${url}.png, ${url}@2x.png`;
}

export function getRelatedProductsIds(product, data = null) {
  const productIds = [];

  if (product.relatedTo) {
    product.relatedTo.forEach(item => {
      if (typeof item === 'string') {
        productIds.push(item);
      }

      if (typeof item === 'object') {
        const id = Object.keys(item)[0];

        if (data) {
          // when related products depend on data
          if (item[id].pages) {
            if (item[id].pages.indexOf(data.pages[0].id) >= 0) {
              productIds.push(id);
            }
          }
        } else {
          productIds.push(id);
        }
      }
    });
  }

  return productIds;
}

export function getUpgradeProductsIds(product) {
  const productIds = [];

  if (product.upgradeTo) {
    product.upgradeTo.forEach(item => {
      if (typeof item === 'string') {
        productIds.push(item);
      }
    });
  }

  return productIds;
}

export function getSkuProductPrices(skuProduct) {
  if (skuProduct.priceTiers) {
    return Object.entries(skuProduct.priceTiers).map(([tier, price]) => {
      return parseInt(tier, 10) * price;
    });
  }
  return [skuProduct.price];
}
